import { useCallback, useEffect, useState } from 'react'

import { Table, Text, VisuallyHidden } from '@loadsmart/loadsmart-ui'

import type { PriceItemType } from 'core/accounting/domain/PriceItemType'
import { RequestStatus } from 'core/domain/Request'
import { formatRate } from 'core/presenters/laneRatePresenter'
import { useInput } from 'core/ui/hooks/useInput'
import { formatCurrency } from 'core/utils/currency'
import { getNumberFromString } from 'core/utils/number'

import { useConfigContext } from 'config/ui/ConfigContext'

import { type AccessorialRate } from '../domain/Accessorial'
import { type Currency } from '../domain/Currency'
import { getCurrencyPrefix } from '../presenters/lanePresenter'
import { useInitialAccessorialRate } from './hooks/useInitialAccessorialRate'
import { useSubmitAccessorialRates } from './hooks/useSubmitAccessorialRates'
import {
  TableCellLabelWrapper,
  RatesTextField,
  NotesTextField,
  TableCellNotesWrapper,
  TableCellLastRateWrapper,
  TableCellShipperNotesWrapper,
} from './RfpAccessorialRow.styles'

export type RfpAccessorialRowProps = {
  rfpId: string
  currency: Currency
  accessorialType?: number
  priceItemType?: PriceItemType
  name: string
  accessorialRates: Array<AccessorialRate>
  onBlur?: (rate: AccessorialRate) => void
  shipperNotes: string
  disabled?: boolean
}

export function RfpAccessorialRow({
  rfpId,
  currency,
  accessorialType,
  priceItemType,
  name,
  accessorialRates,
  onBlur,
  shipperNotes,
  disabled,
}: RfpAccessorialRowProps) {
  const configs = useConfigContext()
  const enablePriceItemTypes = configs.enablePriceItemTypes.toBoolean()

  const initialRate = useInitialAccessorialRate({
    accessorialType,
    priceItemType,
    accessorialRates,
  })
  const accessorialId = enablePriceItemTypes ? priceItemType?.uuid : accessorialType

  const { lastSubmittedRate, submitAccessorialRatesStatus, submitAccessorialRates } =
    useSubmitAccessorialRates(initialRate)

  const [rateId, setRateId] = useState(initialRate.id)

  const { value: accessorialRateValue, setValue: setAccessorialRateValue } = useInput(
    initialRate.rate
  )
  const { value: accessorialNotesValue, setValue: setAccessorialNotesValue } = useInput(
    initialRate.notes
  )

  useEffect(() => {
    setRateId(lastSubmittedRate.id)
  }, [lastSubmittedRate])

  const handleOnBlur = useCallback(() => {
    const controller = new AbortController()

    const rate: AccessorialRate = {
      id: rateId,
      rate: accessorialRateValue.toString(),
      notes: accessorialNotesValue.toString(),
      type: accessorialType,
      priceItemType,
    }

    submitAccessorialRates(rfpId, rate, controller)

    if (onBlur) {
      onBlur(rate)
    }
  }, [
    submitAccessorialRates,
    rfpId,
    rateId,
    accessorialRateValue,
    accessorialNotesValue,
    accessorialType,
    priceItemType,
    onBlur,
  ])

  const isInputDisabled = submitAccessorialRatesStatus === RequestStatus.Pending || disabled
  const currencyPrefix = getCurrencyPrefix(currency)

  return (
    <Table.Row>
      <TableCellLabelWrapper>
        <Text variant="body-bold">{name}</Text>
      </TableCellLabelWrapper>
      <TableCellShipperNotesWrapper alignment="left">
        <Text>{shipperNotes}</Text>
      </TableCellShipperNotesWrapper>
      <TableCellLastRateWrapper alignment="left">
        {initialRate.lastRate
          ? formatCurrency(Number(initialRate.lastRate), false, currencyPrefix)
          : ''}
      </TableCellLastRateWrapper>
      <Table.Cell alignment="right">
        <VisuallyHidden>
          <label htmlFor={`accessorial-rate-${accessorialId}`}>accessorial rate</label>
        </VisuallyHidden>
        <RatesTextField
          id={`accessorial-rate-${accessorialId}`}
          name="accessorialRate"
          placeholder={currencyPrefix}
          value={formatRate(accessorialRateValue, false)}
          onChange={({ currentTarget: { value } }: React.FocusEvent<HTMLInputElement>) => {
            setAccessorialRateValue(getNumberFromString(value))
          }}
          onBlur={handleOnBlur}
          disabled={isInputDisabled}
          scale="small"
        />
      </Table.Cell>
      <TableCellNotesWrapper alignment="right">
        <VisuallyHidden>
          <label htmlFor={`accessorial-notes-${accessorialId}`}>accessorial notes</label>
        </VisuallyHidden>
        <NotesTextField
          id={`accessorial-notes-${accessorialId}`}
          name="accessorialNotes"
          placeholder="Write your notes"
          value={accessorialNotesValue}
          onChange={({ currentTarget: { value } }: React.FocusEvent<HTMLInputElement>) => {
            setAccessorialNotesValue(value)
          }}
          onBlur={handleOnBlur}
          disabled={isInputDisabled}
          scale="small"
        />
      </TableCellNotesWrapper>
    </Table.Row>
  )
}
